import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ADMIN_API } from "../../../common/common";
import { Logout } from "../../../common/Funcation";
import { postApi, sendEvent } from "../../../service";

export interface BetDetailRow {
  matchId: string;
  sportId?: string;
  eventName: string;
  selectionName: string;
  marketName?: string;
  type?: string;
  gameId?: number;
  marketId?: string;
  backAvgOdds: number;
  backStake: number;
  layAvgOdds: number;
  layStake: number;
}

interface MatchListRow {
  _id: string;
  name: string;
  type?: string;
  gameId?: number;
  marketId?: string;
  sportId?: string;
}

interface RawBet {
  selection?: string;
  betSide?: string;
  stake?: number;
  betPlaced?: number;
  oddsUp?: number;
  oddsDown?: number;
  deleted?: boolean;
}

function formatNum(value: number | undefined) {
  return (Number(value) || 0).toFixed(2);
}

function formatSportTitle(value: string | undefined) {
  if (!value) return "Sport";
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}

function toNumber(value: unknown) {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

function formatFixed(value: number) {
  return Number(toNumber(value).toFixed(2));
}

function aggregateMatchBets(
  match: MatchListRow,
  bets: RawBet[],
  marketLabel: string
): BetDetailRow[] {
  const groups = new Map<
    string,
    {
      selection: string;
      backStake: number;
      backOddsSum: number;
      layStake: number;
      layOddsSum: number;
    }
  >();

  const ensureSelection = (selection: string) => {
    const name = String(selection || "").trim();
    if (!name || groups.has(name)) return;
    groups.set(name, {
      selection: name,
      backStake: 0,
      backOddsSum: 0,
      layStake: 0,
      layOddsSum: 0,
    });
  };

  // Prefer team names from the event title so both sides show like the reference UI.
  String(match.name || "")
    .split(/\s+v(?:s)?\.?\s+/i)
    .map((part) => part.trim())
    .filter(Boolean)
    .forEach(ensureSelection);

  bets.forEach((bet) => {
    if (bet.deleted) return;
    const selection = String(bet.selection || "").trim();
    if (!selection) return;
    ensureSelection(selection);

    const group = groups.get(selection)!;
    const stake = toNumber(bet.stake) || toNumber(bet.betPlaced);
    const odds = toNumber(bet.oddsUp) || toNumber(bet.oddsDown);
    const side = String(bet.betSide || "").toLowerCase();

    if (side === "back") {
      group.backStake += stake;
      group.backOddsSum += odds * stake;
    } else if (side === "lay") {
      group.layStake += stake;
      group.layOddsSum += odds * stake;
    }
  });

  return Array.from(groups.values()).map((group) => ({
    matchId: String(match._id),
    sportId: match.sportId || match._id,
    eventName: match.name,
    selectionName: group.selection,
    marketName: marketLabel,
    type: match.type,
    gameId: match.gameId,
    marketId: match.marketId,
    backAvgOdds: group.backStake
      ? formatFixed(group.backOddsSum / group.backStake)
      : 0,
    backStake: formatFixed(group.backStake),
    layAvgOdds: group.layStake
      ? formatFixed(group.layOddsSum / group.layStake)
      : 0,
    layStake: formatFixed(group.layStake),
  }));
}

async function loadSingleMatchDetails(
  match: MatchListRow,
  betType: "odds" | "bookMark"
): Promise<BetDetailRow[]> {
  const marketLabel = betType === "bookMark" ? "Book Maker" : "Odds";
  const betResponse = await postApi({
    api: ADMIN_API.RISK_BET_LIST,
    value: {
      id: match._id,
      betType: [betType],
    },
  });
  const bets: RawBet[] = betResponse.data.data?.betsHistory || [];
  return aggregateMatchBets(match, bets, marketLabel).sort((a, b) =>
    a.selectionName.localeCompare(b.selectionName)
  );
}

async function loadViaExistingApis(
  sportType: string,
  betType: "odds" | "bookMark"
): Promise<BetDetailRow[]> {
  const marketSection = betType === "bookMark" ? "bookMaker" : "matchOdds";
  const marketLabel = betType === "bookMark" ? "Book Maker" : "Odds";

  const listResponse = await postApi({
    api: ADMIN_API.RISK,
    value: {
      marketSection,
      type: sportType,
      page: "1",
      limit: "100",
    },
  });

  const matches: MatchListRow[] = listResponse.data.data?.results || [];
  if (!matches.length) return [];

  const settled = await Promise.all(
    matches.map(async (match) => {
      try {
        const betResponse = await postApi({
          api: ADMIN_API.RISK_BET_LIST,
          value: {
            id: match._id,
            betType: [betType],
          },
        });
        const bets: RawBet[] = betResponse.data.data?.betsHistory || [];
        return aggregateMatchBets(match, bets, marketLabel);
      } catch {
        return [] as BetDetailRow[];
      }
    })
  );

  return settled
    .flat()
    .sort((a, b) => {
      const byEvent = a.eventName.localeCompare(b.eventName);
      if (byEvent !== 0) return byEvent;
      return a.selectionName.localeCompare(b.selectionName);
    });
}

async function loadBetDetails(options: {
  sportType: string;
  betType: "odds" | "bookMark";
  matchId?: string;
  eventName?: string;
  gameId?: string;
  marketId?: string;
}): Promise<BetDetailRow[]> {
  const { sportType, betType, matchId, eventName, gameId, marketId } = options;

  if (matchId) {
    return loadSingleMatchDetails(
      {
        _id: matchId,
        name: eventName || "Match",
        type: sportType,
        gameId: gameId ? Number(gameId) : undefined,
        marketId: marketId || undefined,
        sportId: matchId,
      },
      betType
    );
  }

  try {
    const response = await postApi({
      api: ADMIN_API.RISK_EVENT_SELECTION_AMOUNT,
      value: { type: sportType, betType },
    });
    const results = response.data.data?.results;
    if (Array.isArray(results) && results.length > 0) {
      return results;
    }
  } catch {
    // Remote API may not have this endpoint yet; fall back below.
  }

  return loadViaExistingApis(sportType, betType);
}

function RiskBetDetailsPopup() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sportType = (searchParams.get("type") || "cricket").toLowerCase();
  const betTypeParam =
    searchParams.get("betType") === "bookMark" ? "bookMark" : "odds";
  const matchId = searchParams.get("matchId") || "";
  const eventName = searchParams.get("eventName") || "";
  const gameId = searchParams.get("gameId") || "";
  const marketId = searchParams.get("marketId") || "";

  const [rows, setRows] = useState<BetDetailRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = `${formatSportTitle(sportType)} - Bet Details`;
  }, [sportType]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const results = await loadBetDetails({
          sportType,
          betType: betTypeParam,
          matchId: matchId || undefined,
          eventName: eventName || undefined,
          gameId: gameId || undefined,
          marketId: marketId || undefined,
        });
        if (!cancelled) setRows(results);
      } catch (err: any) {
        if (!cancelled) setRows([]);
        if (err?.response?.data?.statusCode === 401) {
          Logout();
          navigate("/login");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [sportType, betTypeParam, matchId, eventName, gameId, marketId, navigate]);

  const closeWindow = () => {
    window.close();
  };

  const openMatchDetail = (row: BetDetailRow) => {
    const data = {
      eventId: row.gameId,
      marketId: row.marketId,
    };
    dispatch({ type: "CLICKED_GAME", payload: data });
    sendEvent("GET_SPORTS_DETAILS", data);
    navigate("/risk-management-detail/" + (row.sportId || row.matchId));
  };

  const title = formatSportTitle(sportType);

  return (
    <div style={{ display: "flex", justifyContent: "center", background: "#fff", height: "100vh" }}>
      <div className="risk-bet-details-page">
        <div className="risk-bet-details-head">
          <h5>{title}</h5>
          <button type="button" className="risk-bet-details-close" onClick={closeWindow}>
            <span aria-hidden="true">×</span> Close
          </button>
        </div>

        <div className="risk-bet-details-body">
          <table className="risk-bet-details-table">
            <thead>
              <tr>
                <th className="align-L" rowSpan={2}>
                  Event name
                </th>
                <th className="align-L" rowSpan={2} style={{ width: "13%" }}>
                  Selection name
                </th>
                <th className="align-C back-group" colSpan={2} style={{ width: "18%" }}>
                  Back
                </th>
                <th className="align-C lay-group" colSpan={2} style={{ width: "18%" }}>
                  Lay
                </th>
              </tr>
              <tr>
                <th className="align-R back-sub">Avg. odds</th>
                <th className="align-R back-sub">Stake</th>
                <th className="align-R lay-sub">Avg. odds</th>
                <th className="align-R lay-sub">Stake</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="align-C">
                    Loading...
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="align-C">
                    No Data
                  </td>
                </tr>
              ) : (
                rows.map((row, index) => (
                  <tr key={`${row.matchId}-${row.selectionName}-${index}`}>
                    <td className="align-L">
                      <button
                        type="button"
                        className="risk-bet-event-link"
                      // onClick={() => openMatchDetail(row)}
                      >
                        <strong style={{ textDecoration: "underline" }}>{row.eventName}</strong>
                        <img
                          className="fromto"
                          src="data:image/gif;base64,R0lGODdhAQABAIAAAAAAAP///yH5BAEAAAEALAAAAAABAAEAAAICTAEAOw=="
                          alt=""
                        />
                        <span className="market-label">
                          {row.marketName || "Odds"}
                        </span>
                      </button>
                    </td>
                    <td className="align-L">{row.selectionName}</td>
                    <td className="align-R">{formatNum(row.backAvgOdds)}</td>
                    <td className="align-R">
                      <button
                        type="button"
                        className="risk-bet-stake back-stake"
                      // onClick={() => openMatchDetail(row)}
                      >
                        {formatNum(row.backStake)}
                      </button>
                    </td>
                    <td className="align-R">{formatNum(row.layAvgOdds)}</td>
                    <td className="align-R">
                      <button
                        type="button"
                        className="risk-bet-stake lay-stake"
                      // onClick={() => openMatchDetail(row)}
                      >
                        {formatNum(row.layStake)}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default RiskBetDetailsPopup;

function openPopupWindow(url: string, windowName: string) {
  const width = 980;
  const height = 620;
  const left = window.screenX || window.screenLeft || 0;
  const top = window.screenY || window.screenTop || 0;
  const features = [
    `width=${width}`,
    `height=${height}`,
    `left=${left}`,
    `top=${top}`,
    "resizable=yes",
    "scrollbars=yes",
    "toolbar=no",
    "menubar=no",
    "location=no",
    "status=no",
  ].join(",");

  const popup = window.open(url, windowName, features);
  if (popup) {
    popup.focus();
  }
  return popup;
}

export function openRiskBetDetailsWindow(
  sportType: string,
  betType: "odds" | "bookMark" = "odds"
) {
  const type = encodeURIComponent((sportType || "cricket").toLowerCase());
  const url = `/risk-bet-details?type=${type}&betType=${betType}`;
  return openPopupWindow(url, "RiskBetDetailsWindow");
}

export function openRiskMatchBetDetailsWindow(options: {
  matchId: string;
  sportType?: string;
  betType?: "odds" | "bookMark";
  eventName?: string;
  gameId?: number | string;
  marketId?: string;
}) {
  const {
    matchId,
    sportType = "cricket",
    betType = "odds",
    eventName = "",
    gameId = "",
    marketId = "",
  } = options;

  const params = new URLSearchParams({
    type: (sportType || "cricket").toLowerCase(),
    betType,
    matchId: String(matchId),
  });
  if (eventName) params.set("eventName", eventName);
  if (gameId !== "" && gameId != null) params.set("gameId", String(gameId));
  if (marketId) params.set("marketId", String(marketId));

  const url = `/risk-bet-details?${params.toString()}`;
  return openPopupWindow(url, `RiskMatchBetDetails_${matchId}`);
}
